import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Reveal, Overline } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROJECT_TYPES = [
  "Commercial Renovation",
  "Restaurant / Retail Build-Out",
  "Kitchen Remodel",
  "Bathroom Remodel",
  "Full-Home Remodel",
  "Home Addition",
  "Other",
];

const inputCls =
  "w-full bg-brand-navy border border-brand-hi rounded-sm px-4 py-3 text-brand-text placeholder:text-brand-muted/60 focus:outline-none focus:border-brand-orange transition-colors";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", project_type: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.project_type || !form.message) {
      toast.error("Please fill in all fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thanks! We'll be in touch shortly to schedule your consultation.");
      setForm({ name: "", email: "", phone: "", project_type: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again or call us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="relative py-24 md:py-36 bg-brand-navy" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-14 lg:gap-20 items-start">
          {/* Left: headline + info */}
          <div>
            <Reveal>
              <Overline>Get In Touch</Overline>
            </Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-6 font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tighter text-brand-text leading-[1.02]">
                Let's build something that lasts.
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-6 text-lg text-brand-muted max-w-md leading-relaxed">
                Tell us about your project and we'll get back to you with a free,
                no-obligation quote. Serving clients across Florida and Virginia.
              </p>
            </Reveal>

            <div className="mt-12 space-y-5">
              {[
                { icon: Phone, label: "Call us", value: "786-564-3401", href: "tel:7865643401", testid: "contact-phone" },
                { icon: Mail, label: "Email us", value: "damianoffice01@gmail.com", href: "mailto:damianoffice01@gmail.com", testid: "contact-email" },
                { icon: MapPin, label: "Service area", value: "Florida & Virginia", href: null, testid: "contact-area" },
              ].map((c) => (
                <Reveal key={c.label} delay={0.05}>
                  <a
                    href={c.href || undefined}
                    className="group flex items-center gap-4 border border-brand-hi rounded-sm p-5 bg-brand-surface hover:border-brand-orange/50 transition-colors"
                    data-testid={c.testid}
                  >
                    <span className="w-11 h-11 flex items-center justify-center border border-brand-hi rounded-sm text-brand-orange shrink-0">
                      <c.icon className="w-5 h-5" strokeWidth={1.5} />
                    </span>
                    <span>
                      <span className="block text-xs uppercase tracking-widest text-brand-muted">{c.label}</span>
                      <span className="block text-brand-text font-medium mt-0.5">{c.value}</span>
                    </span>
                  </a>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.1}>
              <p className="mt-8 text-sm text-brand-muted">
                Founder & CEO — <span className="text-brand-text font-medium">Damian Gonzalez-Perez</span>
              </p>
            </Reveal>
          </div>

          {/* Right: form */}
          <Reveal delay={0.1}>
            <motion.form
              onSubmit={onSubmit}
              className="border border-brand-hi rounded-sm bg-brand-surface p-6 md:p-10"
              data-testid="contact-form"
            >
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm text-brand-muted mb-2">Name</label>
                  <input className={inputCls} placeholder="John Doe" value={form.name} onChange={update("name")} data-testid="input-name" />
                </div>
                <div>
                  <label className="block text-sm text-brand-muted mb-2">Phone</label>
                  <input className={inputCls} placeholder="(000) 000-0000" value={form.phone} onChange={update("phone")} data-testid="input-phone" />
                </div>
              </div>

              <div className="mt-5">
                <label className="block text-sm text-brand-muted mb-2">Email</label>
                <input type="email" className={inputCls} placeholder="you@email.com" value={form.email} onChange={update("email")} data-testid="input-email" />
              </div>

              <div className="mt-5">
                <label className="block text-sm text-brand-muted mb-2">Project Type</label>
                <Select value={form.project_type} onValueChange={(v) => setForm((f) => ({ ...f, project_type: v }))}>
                  <SelectTrigger
                    className="h-auto bg-brand-navy border-brand-hi rounded-sm px-4 py-3 text-brand-text focus:ring-0 focus:border-brand-orange data-[placeholder]:text-brand-muted/60"
                    data-testid="select-project-type"
                  >
                    <SelectValue placeholder="Select a project type" />
                  </SelectTrigger>
                  <SelectContent className="bg-brand-surface border-brand-hi text-brand-text">
                    {PROJECT_TYPES.map((t) => (
                      <SelectItem key={t} value={t} className="focus:bg-brand-hi focus:text-brand-text" data-testid={`option-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                        {t}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="mt-5">
                <label className="block text-sm text-brand-muted mb-2">Message</label>
                <textarea
                  rows={4}
                  className={`${inputCls} resize-none`}
                  placeholder="Tell us about your project, timeline, and location..."
                  value={form.message}
                  onChange={update("message")}
                  data-testid="input-message"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="group mt-8 w-full inline-flex items-center justify-center gap-3 bg-brand-orange hover:bg-brand-orangeHover disabled:opacity-70 text-white font-semibold px-8 py-4 rounded-sm transition-colors"
                data-testid="contact-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Sending...
                  </>
                ) : (
                  <>
                    Schedule a Consultation
                    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" strokeWidth={2} />
                  </>
                )}
              </button>
            </motion.form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
